/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  UserSessionWorkloadAdmission,
  UserSessionWorkloadAdmission$inboundSchema,
} from "./usersessionworkloadadmission.js";

/**
 * Lifecycle state of the assigned agent.
 */
export const AgentStatus = {
  Active: "active",
  Suspended: "suspended",
  Revoked: "revoked",
} as const;
/**
 * Lifecycle state of the assigned agent.
 */
export type AgentStatus = OpenEnum<typeof AgentStatus>;

/**
 * The workload behind a workload session.
 */
export type UserSessionWorkload = {
  /**
   * Every admission currently letting this workload in, from this project and from the organization. Withdrawing one leaves the others admitting it. Empty when nothing admits the workload any more.
   */
  admissions: Array<UserSessionWorkloadAdmission>;
  /**
   * The agent this workload is assigned to, whose policy it inherits. Null when the workload has no live assignment.
   */
  agentId?: string | undefined;
  /**
   * Name of the assigned agent.
   */
  agentName?: string | undefined;
  /**
   * Lifecycle state of the assigned agent.
   */
  agentStatus?: AgentStatus | undefined;
  /**
   * The sub claim the workload issuer asserted, exactly as minted. Together with workload_issuer_id this is the workload's identity.
   */
  externalSubject: string;
  /**
   * The workload_issuers row that vouched for the workload.
   */
  workloadIssuerId: string;
  /**
   * The operator-chosen name of the workload issuer. Null when the issuer has been deleted or belongs to another project.
   */
  workloadIssuerName?: string | undefined;
  /**
   * The workload issuer's issuer identifier (its iss). Null under the same conditions as workload_issuer_name.
   */
  workloadIssuerUrl?: string | undefined;
};

/** @internal */
export const AgentStatus$inboundSchema: z.ZodMiniType<AgentStatus, unknown> =
  openEnums.inboundSchema(AgentStatus);

/** @internal */
export const UserSessionWorkload$inboundSchema: z.ZodMiniType<
  UserSessionWorkload,
  unknown
> = z.pipe(
  z.object({
    admissions: z.array(UserSessionWorkloadAdmission$inboundSchema),
    agent_id: z.optional(z.string()),
    agent_name: z.optional(z.string()),
    agent_status: z.optional(AgentStatus$inboundSchema),
    external_subject: z.string(),
    workload_issuer_id: z.string(),
    workload_issuer_name: z.optional(z.string()),
    workload_issuer_url: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "agent_id": "agentId",
      "agent_name": "agentName",
      "agent_status": "agentStatus",
      "external_subject": "externalSubject",
      "workload_issuer_id": "workloadIssuerId",
      "workload_issuer_name": "workloadIssuerName",
      "workload_issuer_url": "workloadIssuerUrl",
    });
  }),
);

export function userSessionWorkloadFromJSON(
  jsonString: string,
): SafeParseResult<UserSessionWorkload, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => UserSessionWorkload$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'UserSessionWorkload' from JSON`,
  );
}
